import { apiRequest } from "@services/apiClient";

/**
 * A clinic's synced providers, as the admin clinic drawer manages them:
 * role, default, working days. These are the admin-only clinic endpoints;
 * the campaign start modal keeps reading services/reactivation.fetchProviders.
 *
 * Row shape: { external_provider_id, name, is_default, role ("dentist" |
 * "hygienist" | null), role_source ("pms" | "manual" | null), specialty,
 * status, manual_schedule, manual_schedule_updated_at, learned_schedule,
 * schedule_source ("manual" | "learned" | "none") }.
 */

const base = (clinicId) => `/scheduling/clinics/${encodeURIComponent(clinicId)}/providers`;
const one = (clinicId, externalProviderId) =>
  `${base(clinicId)}/${encodeURIComponent(externalProviderId)}`;

/** Every provider of the clinic. Always an array. */
export async function fetchClinicProviders(clinicId) {
  const response = await apiRequest({ method: "GET", endpoint: base(clinicId) });
  return Array.isArray(response?.data) ? response.data : [];
}

/**
 * Set the role ("dentist" | "hygienist" | null for unassigned), or with
 * `reset: true` hand it back to what the PMS type says. Resolves to the
 * updated row, or null.
 */
export async function setClinicProviderRole({ clinicId, externalProviderId, role, reset = false }) {
  const response = await apiRequest({
    method: "PUT",
    endpoint: `${one(clinicId, externalProviderId)}/role`,
    body: reset ? { reset: true } : { role },
  });
  return response?.data?.provider ?? null;
}

/** Make one provider the clinic's default. Resolves to the new list. */
export async function setClinicDefaultProvider({ clinicId, externalProviderId }) {
  const response = await apiRequest({
    method: "PUT",
    endpoint: `${base(clinicId)}/default`,
    body: { external_provider_id: externalProviderId },
  });
  return Array.isArray(response?.data) ? response.data : [];
}

/**
 * Store the rota an admin entered for one provider, or clear it with
 * `weekdays: null` so the provider falls back to the learned pattern.
 * Resolves to the updated row, or null.
 */
export async function setClinicProviderSchedule({ clinicId, externalProviderId, weekdays }) {
  const response = await apiRequest({
    method: "PUT",
    endpoint: `${one(clinicId, externalProviderId)}/schedule`,
    body: { weekdays },
  });
  return response?.data?.provider ?? null;
}

// ---------------------------------------------------------------------------
// The connected inbound flow (spec 2026-10-14): what the agent says a
// provider is called, who may be booked by phone, which procedures each
// provider does, the clinic's visit types, the learned profile, the
// connected-flow switch and the Harbor key check.
// ---------------------------------------------------------------------------

const clinic = (clinicId) => `/scheduling/clinics/${encodeURIComponent(clinicId)}`;

/**
 * Store the spoken name (null = derive it from the PMS name) and/or the
 * bookable-by-phone choice (true | false | null = automatic). Only the keys
 * given are sent. Resolves to the updated row, or null.
 */
export async function setClinicProviderProfile({ clinicId, externalProviderId, spokenName, bookableByPhone }) {
  const body = {};
  if (spokenName !== undefined) body.spoken_name = spokenName;
  if (bookableByPhone !== undefined) body.bookable_by_phone = bookableByPhone;
  const response = await apiRequest({
    method: "PUT", endpoint: `${one(clinicId, externalProviderId)}/profile`, body,
  });
  return response?.data?.provider ?? null;
}

/**
 * Replace the procedures a provider does (visit type ids), or with
 * `reset: true` hand them back to what learning found. Resolves to the
 * updated row, or null.
 */
export async function setClinicProviderVisitTypes({ clinicId, externalProviderId, visitTypeIds, reset = false }) {
  const response = await apiRequest({
    method: "PUT",
    endpoint: `${one(clinicId, externalProviderId)}/visit-types`,
    body: reset ? { reset: true } : { visit_type_ids: visitTypeIds },
  });
  return response?.data?.provider ?? null;
}

/** The clinic's active visit types. Always an array. */
export async function fetchVisitTypes(clinicId) {
  const response = await apiRequest({ method: "GET", endpoint: `${clinic(clinicId)}/visit-types` });
  return Array.isArray(response?.data) ? response.data : [];
}

/** Add a visit type; `fields` are the row's editable fields. Resolves to the row, or null. */
export async function createVisitType({ clinicId, fields }) {
  const response = await apiRequest({ method: "POST", endpoint: `${clinic(clinicId)}/visit-types`, body: fields });
  return response?.data ?? null;
}

/** Change some fields of a visit type (`reset_length: true` hands the length back to learning). */
export async function updateVisitType({ clinicId, visitTypeId, fields }) {
  const response = await apiRequest({
    method: "PUT", endpoint: `${clinic(clinicId)}/visit-types/${encodeURIComponent(visitTypeId)}`, body: fields,
  });
  return response?.data ?? null;
}

/** Retire a visit type (it stays on past bookings). Resolves to the row, or null. */
export async function deactivateVisitType({ clinicId, visitTypeId }) {
  const response = await apiRequest({
    method: "DELETE", endpoint: `${clinic(clinicId)}/visit-types/${encodeURIComponent(visitTypeId)}`,
  });
  return response?.data ?? null;
}

/** The learned profile summary: coverage, warnings, suggestions, the review and key-check state. */
export async function fetchLearning(clinicId) {
  const response = await apiRequest({ method: "GET", endpoint: `${clinic(clinicId)}/learning` });
  return response?.data ?? null;
}

/** Learn again now, from the mirrored history. Resolves to the new summary. */
export async function runLearning(clinicId) {
  const response = await apiRequest({ method: "POST", endpoint: `${clinic(clinicId)}/learning/run` });
  return response?.data ?? null;
}

/** An admin has looked at the learned profile: the connected flow's one-time gate. */
export async function markProfileReviewed(clinicId) {
  const response = await apiRequest({ method: "POST", endpoint: `${clinic(clinicId)}/learning/reviewed` });
  return response?.data ?? null;
}

/**
 * Switch the connected flow on or off. The server refuses to switch it on
 * before the profile is reviewed or while the key check fails; the thrown
 * error carries its reason.
 */
export async function setConnectedFlow({ clinicId, enabled }) {
  const response = await apiRequest({
    method: "PUT", endpoint: `${clinic(clinicId)}/connected-flow`, body: { enabled },
  });
  return response?.data ?? null;
}

/** Ask Harbor whether the key still holds every scope; `probeWrites` also probes the write scopes. */
export async function runKeyCheck({ clinicId, probeWrites = false }) {
  const response = await apiRequest({
    method: "POST", endpoint: `${clinic(clinicId)}/key-check`, body: { probe_writes: probeWrites },
  });
  return response?.data ?? null;
}
